import type {
  GetWorkspacePublicPageByIdQuery,
  PublicPageMode,
} from '@pique/graphql';
import type { DocService, WorkspaceService } from '@toeverything/infra';
import {
  backoffRetry,
  catchErrorInto,
  effect,
  Entity,
  fromPromise,
  LiveData,
  mapInto,
  onComplete,
  onStart,
} from '@toeverything/infra';
import { switchMap } from 'rxjs';

import { isBackendError, isNetworkError } from '../../cloud';
import type { ShareStore } from '../stores/share';

type ShareInfoType = GetWorkspacePublicPageByIdQuery['workspace']['publicPage'];

export class ShareInfo extends Entity {
  info$ = new LiveData<ShareInfoType | undefined | null>(null);
  isShared$ = this.info$.map((info: ShareInfoType | undefined | null) =>
    // null means not loaded yet, undefined means not shared
    info !== null ? info !== undefined : null
  );
  sharedMode$ = this.info$.map((info: ShareInfoType | undefined | null) =>
    info !== null ? info?.mode : null
  );

  error$ = new LiveData<unknown>(null);
  isRevalidating$ = new LiveData<boolean>(false);

  constructor(
    private readonly workspaceService: WorkspaceService,
    private readonly docService: DocService,
    private readonly store: ShareStore
  ) {
    super();
  }

  revalidate = effect(
    switchMap(() => {
      return fromPromise((signal: AbortSignal) =>
        this.store.getShareInfoByDocId(
          this.workspaceService.workspace.id,
          this.docService.doc.id,
          signal
        )
      ).pipe(
        backoffRetry({
          when: isNetworkError,
          count: Infinity,
        }),
        backoffRetry({
          when: isBackendError,
        }),
        mapInto(this.info$),
        catchErrorInto(this.error$),
        onStart(() => {
          this.isRevalidating$.next(true);
        }),
        onComplete(() => {
          this.isRevalidating$.next(false);
        })
      );
    })
  );

  waitForRevalidation(signal?: AbortSignal) {
    this.revalidate();
    return this.isRevalidating$.waitFor((v: boolean) => v === false, signal);
  }

  getShareInfoForDoc(pageId: string, signal?: AbortSignal) {
    return this.store.getShareInfoByDocId(
      this.workspaceService.workspace.id,
      pageId,
      signal
    );
  }

  async enableShare(mode: PublicPageMode) {
    await this.enableShareForDoc(this.docService.doc.id, mode);
  }

  async enableShareForDoc(pageId: string, mode: PublicPageMode) {
    await this.store.enableSharePage(
      this.workspaceService.workspace.id,
      pageId,
      mode
    );
    if (pageId === this.docService.doc.id) {
      await this.waitForRevalidation();
    }
  }

  async changeShare(mode: PublicPageMode) {
    await this.enableShare(mode);
  }

  async disableShare() {
    await this.disableShareForDoc(this.docService.doc.id);
  }

  async disableShareForDoc(pageId: string) {
    await this.store.disableSharePage(
      this.workspaceService.workspace.id,
      pageId
    );
    if (pageId === this.docService.doc.id) {
      await this.waitForRevalidation();
    }
  }
}
